import { ChangeEvent, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { ScholarshipCardSkeleton } from '@components/ScholarshipCard/ScholarshipCardSkeleton';
import { CoreBox } from '@youscience/core';
import { isUndefined } from 'lodash-es';

import ScholarshipCard from '@components/ScholarshipCard';
import { EmptyStateText } from '@components/EmptyStateText';
import { Pagination } from '@components/Pagination';

import { ROUTES } from '@constants/ROUTES';
import { PAGE_TABS_DATA, SortByOptions, SCHOLARSHIP_CARD_SKELETONS } from '@constants/scholarships';
import { MOBILE_PER_PAGE_OPTIONS } from '@constants';

import useUserPreferencesStore from '@store/UserPreferencesStore';
import useFavoriteScholarshipsStore from '@store/FavoriteScholarshipsStore';

import { getSortedScholarships } from '@utils/scholarships';

import MediumTargetIcon from '@images/medium-target.svg?react';

import SortBy from '../SortBy';

import { StyledActionsWrap, StyledScholarshipCardsWrap } from './Favorites.styles';

const MAX_CARDS_COUNT = 20;

export const Favorites = () => {
  const scholarshipFavorites = useUserPreferencesStore((state) => state.scholarshipFavorites);
  const favoriteScholarships = useUserPreferencesStore((state) => state.userPreferences.favorites?.scholarships);

  const filters = useFavoriteScholarshipsStore((state) => state.filters);

  const { setSearchFilters, sortByValues, setSortByFields } = useFavoriteScholarshipsStore((state) => state);

  const limitValue = Number(filters.limit) || MOBILE_PER_PAGE_OPTIONS[0];

  const [currentPage, setCurrentPage] = useState((Number(filters.skip) || 0) / limitValue + 1);
  const [paginationLimit, setPaginationLimit] = useState(limitValue);

  const listRange = {
    from: currentPage === 1 ? 0 : limitValue * (currentPage - 1),
    to: currentPage === 1 ? limitValue : limitValue * currentPage,
  };

  const resetPaginationPage = () => {
    setSearchFilters({ skip: 0 });
    setCurrentPage(1);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<unknown>) => {
    const newLimit = Number(event.target.value);

    setSearchFilters({ limit: newLimit });
    resetPaginationPage();
    setPaginationLimit(newLimit);
  };

  const handlePaginationChange = (event: ChangeEvent<unknown>, page: number) => {
    const skip = (page - 1) * paginationLimit;

    setSearchFilters({ skip });

    setCurrentPage(page);
  };

  const sortedScholarships = useMemo(
    () => getSortedScholarships(scholarshipFavorites, sortByValues),
    [scholarshipFavorites, sortByValues],
  );

  return (
    <CoreBox>
      <StyledActionsWrap>
        <SortBy sortByValues={sortByValues} sortByOptions={SortByOptions} setSortByFields={setSortByFields} />

        {sortedScholarships.length > MAX_CARDS_COUNT && (
          <Pagination
            totalCount={sortedScholarships.length}
            limit={paginationLimit}
            page={currentPage}
            selectPerPageOptions={MOBILE_PER_PAGE_OPTIONS}
            isLoading={false}
            onPaginationChange={handlePaginationChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </StyledActionsWrap>

      <StyledScholarshipCardsWrap>
        {isUndefined(favoriteScholarships) &&
          SCHOLARSHIP_CARD_SKELETONS.map((skeletonCard) => {
            return <ScholarshipCardSkeleton key={skeletonCard.id} isWiderCardType />;
          })}

        {sortedScholarships
          .slice(listRange.from, listRange.to)
          ?.map((scholarship) => <ScholarshipCard key={scholarship.id} isWiderCardType scholarship={scholarship} />)}
      </StyledScholarshipCardsWrap>

      {!isUndefined(favoriteScholarships) && sortedScholarships.length === 0 && (
        <EmptyStateText
          icon={<MediumTargetIcon />}
          title="No favorite scholarships yet"
          subtitle="Hmmm. Looks like you haven't favorited any scholarships."
          buttonTitle="Start exploring"
          buttonLink={`${ROUTES.SCHOLARSHIPS}#${PAGE_TABS_DATA.allScholarships.hash}`}
        />
      )}
    </CoreBox>
  );
};
